import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TwoFactorPrompt from '../components/TwoFactorPrompt';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [showTwoFA, setShowTwoFA] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const { login, validate2FA } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result && result.twoFaRequired) {
        // Show 2FA prompt
        setPendingUser(result.user);
        setShowTwoFA(true);
        setLoading(false);
        return;
      }

      // Normal login success
      navigate('/');
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = err.response.data?.retry_after || 60;
        setError(`Слишком много попыток. Повторите через ${retryAfter} сек.`);
      } else {
        setError(err.response?.data?.error || 'Login error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (code, recoveryCode) => {
    setTwoFAError('');
    setTwoFALoading(true);

    try {
      const result = await validate2FA(code, recoveryCode);

      // Show warning if low on recovery codes
      if (result.recoveryCodesRemaining !== undefined && result.recoveryCodesRemaining <= 3) {
        // Could show a notification, but for now just proceed
        console.log(`Warning: Only ${result.recoveryCodesRemaining} recovery codes remaining`);
      }

      navigate('/');
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = err.response.data?.retry_after || 60;
        setTwoFAError(`Слишком много попыток. Повторите через ${retryAfter} сек.`);
      } else {
        const errMsg = err.response?.data?.error || 'Invalid code';
        setTwoFAError(errMsg);
      }
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleCancel2FA = () => {
    setShowTwoFA(false);
    setPendingUser(null);
    setTwoFAError('');
    localStorage.removeItem('token');
  };

  return (
    <div className="login-container">
      <div className="mesh-bg" />
      <div className="grid-bg" />
      <div className="login-card" style={showTwoFA ? { maxWidth: '440px' } : undefined}>
        <div className="login-logo">
          <div className="login-logo-icon">
            <svg width="28" height="28" fill="none" stroke="#fff" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </div>
          <div className="login-title">TH Tools</div>
        </div>

        {showTwoFA ? (
          <div style={{ padding: '0 10px' }}>
            {pendingUser && (
              <p style={{ color: '#6b7a9f', fontSize: '0.85rem', textAlign: 'center', marginBottom: '20px' }}>
                Logging in as {pendingUser.email}
              </p>
            )}
            <TwoFactorPrompt
              onSubmit={handle2FASubmit}
              onCancel={handleCancel2FA}
              error={twoFAError}
              loading={twoFALoading}
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="login-error">{error}</div>}

            <div className="login-field">
              <label className="login-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                placeholder="admin@example.com"
                required
              />
            </div>

            <div className="login-field">
              <label className="login-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="login-btn"
              style={{ opacity: loading ? 0.7 : 1 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
